import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../supabase';

/**
 * useEnergyData — fetches the current user's recent reflection-engine logs.
 *
 * The reflection engine logs ABOUT yesterday. When the user has already
 * submitted today (i.e. a row with log_date === yesterday's local date
 * exists), we surface that row as `yesterdayLog` so the UI can show the
 * cached analysis_text instead of re-rendering the form.
 *
 * Returns:
 *   {
 *     userId,
 *     yesterdayLog,  // row where log_date === yesterday, or null
 *     last14Days,    // array of recent rows, log_date desc
 *     loading,
 *     error,
 *     refresh,
 *   }
 */
export default function useEnergyData() {
  const [userId, setUserId] = useState(null);
  const [last14Days, setLast14Days] = useState([]);
  const [yesterdayLog, setYesterdayLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const uid = userData?.user?.id || null;
      setUserId(uid);
      if (!uid) {
        setLast14Days([]);
        setYesterdayLog(null);
        return;
      }

      const { data, error: qErr } = await supabase
        .from('energy_logs')
        .select('id, log_date, sleep_hours, deep_work_hours, meals_skipped, movement, would_repeat, analysis_text, analysis_structured, logged_at')
        .eq('user_id', uid)
        .order('log_date', { ascending: false })
        .limit(14);

      if (qErr) throw qErr;

      const rows = data || [];
      setLast14Days(rows);

      const yKey = yesterdayLocal();
      setYesterdayLog(rows.find((r) => r.log_date === yKey) || null);
    } catch (e) {
      setError(e?.message || 'Failed to load energy data');
      setLast14Days([]);
      setYesterdayLog(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return {
    userId,
    yesterdayLog,
    last14Days,
    loading,
    error,
    refresh: load,
  };
}

export function yesterdayLocal() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return localDate(d);
}

function localDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
